/**
 * Lógica compartida de los DEMOS de OPUS ci (pura, sin three ni web-ifc).
 *
 * Los conceptos, precios y avances de los demos son DE EJEMPLO y viven en `datos/*.json`. Aquí solo
 * está cómo se cruzan con el modelo real: un concepto de ejemplo trae un "selector" (clase IFC, tipo
 * predefinido, nivel, posición) y la cantidad sale de sumar las medidas reales de lo que selecciona.
 */
import type { ElementoDemo } from './modeloDemo.js';
import hpEjemplo from './datos/hp-ejemplo.json';
import { brandingProductoActivo } from '../../lib/brandingDelProducto.js';
import type { ConfKind } from './ConfianzaCotizacion.js';

export type Magnitud = 'area' | 'longitud' | 'volumen' | 'piezas';

export interface Selector {
  clases?: string[];
  /** Tipos del modelo (como los asignaría el usuario: tipo → concepto). */
  tipos?: string[];
  /** PredefinedType del IFC que debe tener (cualquiera de la lista). */
  predef?: string[];
  /** PredefinedType que NO debe tener. */
  predefNo?: string[];
  /** Nivel por orden de elevación (0 = el más bajo). */
  nivel?: number;
  /** Muro en la orilla del edificio (perímetro) o adentro. Se mide contra la caja del conjunto. */
  posicion?: 'perimetro' | 'interior';
}

export interface ConceptoEjemplo {
  id: string;
  partida: string;
  clave: string;
  descripcion: string;
  unidad: string;
  magnitud: Magnitud;
  pu: number;
  /** Mismos niveles que la columna Confianza de la cotización. */
  confianza: ConfKind;
  selector: Selector;
}

export interface PartidaEjemplo {
  id: string;
  nombre: string;
  color: string;
}

export const HP_EJEMPLO = hpEjemplo as {
  aviso: string;
  partidas: PartidaEjemplo[];
  conceptos: ConceptoEjemplo[];
};

// --- Posición en planta ------------------------------------------------------------------------------

export interface CajaPlanta {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export function cajaPlanta(els: readonly ElementoDemo[]): CajaPlanta | null {
  let c: CajaPlanta | null = null;
  for (const e of els) {
    if (!e.caja) continue;
    const [x0, , z0] = e.caja.min;
    const [x1, , z1] = e.caja.max;
    if (!c) c = { minX: x0, maxX: x1, minZ: z0, maxZ: z1 };
    else {
      c.minX = Math.min(c.minX, x0);
      c.maxX = Math.max(c.maxX, x1);
      c.minZ = Math.min(c.minZ, z0);
      c.maxZ = Math.max(c.maxZ, z1);
    }
  }
  return c;
}

/**
 * ¿La pieza corre por la orilla del conjunto? Su lado largo tiene que quedar pegado al borde de la caja
 * en planta, con holgura igual a SU PROPIO espesor (el lado corto). Sin números fijos: se compara la
 * pieza consigo misma.
 */
export function vaPorElPerimetro(e: ElementoDemo, caja: CajaPlanta): boolean {
  if (!e.caja) return false;
  const [x0, , z0] = e.caja.min;
  const [x1, , z1] = e.caja.max;
  const dx = x1 - x0;
  const dz = z1 - z0;
  if (dx >= dz) return z0 - caja.minZ <= dz || caja.maxZ - z1 <= dz;
  return x0 - caja.minX <= dx || caja.maxX - x1 <= dx;
}

/** Contexto para evaluar selectores: la caja en planta de cada juego de clases. */
export function crearContexto(elementos: readonly ElementoDemo[]) {
  const cache = new Map<string, CajaPlanta | null>();
  return {
    cajaDe(clases: string[] | undefined): CajaPlanta | null {
      const k = (clases ?? []).join(',');
      if (!cache.has(k)) cache.set(k, cajaPlanta(elementos.filter((e) => !clases || clases.includes(e.clase))));
      return cache.get(k) ?? null;
    },
  };
}
export type ContextoDemo = ReturnType<typeof crearContexto>;

export function coincide(e: ElementoDemo, s: Selector, ctx: ContextoDemo): boolean {
  if (s.clases && !s.clases.includes(e.clase)) return false;
  if (s.tipos && !s.tipos.includes(e.tipo)) return false;
  if (s.predef && !s.predef.includes(e.predef)) return false;
  if (s.predefNo && s.predefNo.includes(e.predef)) return false;
  if (s.nivel != null && e.nivelOrden !== s.nivel) return false;
  if (s.posicion) {
    const caja = ctx.cajaDe(s.clases);
    if (!caja) return false;
    const orilla = vaPorElPerimetro(e, caja);
    if ((s.posicion === 'perimetro') !== orilla) return false;
  }
  return true;
}

/**
 * Cantidad de una pieza en la magnitud del concepto. Si el modelo declara cantidades pero ninguna de
 * esa magnitud (p. ej. cabrios con volumen pero sin largo), se toma la de su malla, igual que ci hace
 * cuando la pieza no declara nada.
 */
export function cantidadDe(e: ElementoDemo, m: Magnitud): number {
  if (m === 'piezas') return 1;
  return e[m] ?? e.geo[m] ?? 0;
}

/** true si la cantidad de esa magnitud salió del dibujo y no de lo que declara el modelo. */
export const cantidadEstimada = (e: ElementoDemo, m: Magnitud) => m !== 'piezas' && (e.estimado || e[m] == null);

/** Concepto de ejemplo al que va un elemento (el primero cuyo selector lo toma). */
export function conceptoDe(
  e: ElementoDemo,
  ctx: ContextoDemo,
  conceptos: readonly ConceptoEjemplo[] = HP_EJEMPLO.conceptos,
): ConceptoEjemplo | null {
  return conceptos.find((c) => coincide(e, c.selector, ctx)) ?? null;
}

// --- Formato -----------------------------------------------------------------------------------------

const PESOS = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
const NUM2 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NUM0 = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });

export const pesos = (x: number) => PESOS.format(x);
export const num2 = (x: number) => NUM2.format(x);
export const num0 = (x: number) => NUM0.format(x);
export const cant = (x: number, m: Magnitud) => (m === 'piezas' ? num0(x) : num2(x));

/** Color de un token del design system (CSS var) como número para three.js. */
export function colorDeToken(nombre: string, respaldo: string): number {
  let v = '';
  try {
    v = getComputedStyle(document.documentElement).getPropertyValue(nombre).trim();
  } catch {
    v = '';
  }
  return colorHex(v || respaldo);
}

export function colorHex(css: string): number {
  const s = css.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return parseInt(s.slice(1), 16);
  if (/^#[0-9a-f]{3}$/i.test(s)) return parseInt(s.slice(1).split('').map((c) => c + c).join(''), 16);
  const m = s.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const [r, g, b] = m[1]!.split(',').map((x) => parseFloat(x));
    return ((r ?? 0) << 16) | ((g ?? 0) << 8) | (b ?? 0);
  }
  return 0x888888;
}

/** Colores funcionales de los demos, tomados de los tokens (verde/ámbar/rojo del sistema). */
export function coloresDemo() {
  return {
    ok: colorDeToken('--ok', '#10b981'),
    warn: colorDeToken('--warn', '#f59e0b'),
    bad: colorDeToken('--bad', '#f3604c'),
    neutro: colorDeToken('--opus-neutral-60', '#6c7789'),
    acento: colorHex(brandingProductoActivo('ci').c1),
  };
}

/**
 * Nombre en español de cada clase del estándar IFC (traducción de la taxonomía FIJA del formato, no un
 * dato de obra). El nombre propio de la pieza sale del modelo.
 */
export const NOMBRE_CLASE: Readonly<Record<string, string>> = {
  IfcWall: 'Muro',
  IfcWallStandardCase: 'Muro',
  IfcSlab: 'Losa',
  IfcColumn: 'Columna',
  IfcBeam: 'Viga',
  IfcDoor: 'Puerta',
  IfcWindow: 'Ventana',
  IfcStair: 'Escalera',
  IfcRoof: 'Techo',
  IfcCovering: 'Recubrimiento',
  IfcRailing: 'Barandal',
  IfcFooting: 'Zapata',
  IfcMember: 'Elemento de estructura',
  IfcPlate: 'Placa',
  IfcCurtainWall: 'Muro cortina',
  IfcFurnishingElement: 'Mueble',
  IfcFurniture: 'Mueble',
  IfcBuildingElementProxy: 'Elemento genérico',
  IfcRamp: 'Rampa',
  IfcPile: 'Pilote',
  IfcSpace: 'Espacio (cuarto)',
  IfcOpeningElement: 'Vano',
  IfcVirtualElement: 'Límite virtual',
  IfcAnnotation: 'Anotación',
};

export const nombreClase = (c: string) => NOMBRE_CLASE[c] ?? c;
export const etiquetaElemento = (e: ElementoDemo) => `${nombreClase(e.clase)} ${e.nombre ? `«${e.nombre}»` : `#${e.eid}`}`;

/** Redondeo a centímetros (la precisión con la que ci reporta medidas), para comparar sin ruido numérico. */
export const r2cm = (x: number) => Math.round(x * 100) / 100;
